import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgeApi, lmsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import RichContentEditor from '../../components/RichContentEditor';
import { toast } from 'sonner';
import { Plus, Loader2, Pencil, Trash2, BookOpen, Eye, FolderPlus, Upload } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function KnowledgeAdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editingCat, setEditingCat] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        knowledgeApi.listArticles(),
        knowledgeApi.listCategories(),
      ]);
      setArticles(a.data); setCategories(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveArticle = async () => {
    try {
      const payload = {
        title: editing.title,
        summary: editing.summary || '',
        content_html: editing.content_html || '',
        category_id: editing.category_id || null,
        featured_image_url: editing.featured_image_url || null,
        status: editing.status || 'draft',
        tags: (editing.tags || '').split(',').map(t => t.trim()).filter(Boolean),
      };
      if (editing.id) await knowledgeApi.updateArticle(editing.id, payload);
      else await knowledgeApi.createArticle(payload);
      toast.success('Article saved');
      setEditing(null);
      await load();
    } catch (err) { toast.error('Save failed'); }
  };

  const saveCat = async () => {
    try {
      const payload = { name: editingCat.name, description: editingCat.description || '', icon: editingCat.icon || 'BookOpen', order: editingCat.order || 0 };
      if (editingCat.id) await knowledgeApi.updateCategory(editingCat.id, payload);
      else await knowledgeApi.createCategory(payload);
      toast.success('Category saved');
      setEditingCat(null);
      await load();
    } catch (err) { toast.error('Save failed'); }
  };

  const uploadFeaturedImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await lmsApi.upload('images', file);
      setEditing({ ...editing, featured_image_url: `${BACKEND}${data.url}` });
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { e.target.value = ''; }
  };

  return (
    <div className="space-y-6" data-testid="knowledge-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <BookOpen className="w-6 h-6 text-[#0E7490]" />Knowledge Base
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Articles and reference material with full rich-content authoring.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="articles" data-testid="kb-tab-articles">Articles ({articles.length})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="kb-tab-categories">Categories ({categories.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="articles" className="space-y-4">
          <Button className="careiq-btn-primary" onClick={() => setEditing({ title: '', status: 'draft', content_html: '', tags: '' })} data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-1" />New Article
          </Button>
          {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map(a => (
                <Card key={a.id} className="careiq-card overflow-hidden" data-testid={`kb-card-${a.id}`}>
                  {a.featured_image_url ? (
                    <img src={a.featured_image_url} alt="" className="w-full h-36 object-cover" />
                  ) : (
                    <div className="w-full h-36 bg-gradient-to-br from-[#0E7490]/10 to-[#0A2342]/10 flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-[#94A3B8]" />
                    </div>
                  )}
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-[#0A2342] line-clamp-2 flex-1">{a.title}</h3>
                      <Badge className={a.status === 'published' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#FEF3C7] text-[#92400E]'}>{a.status}</Badge>
                    </div>
                    <div className="text-xs text-[#64748B]">{a.author_name} · {a.view_count} views</div>
                    <p className="text-xs text-[#64748B] line-clamp-2">{a.summary}</p>
                    <div className="flex gap-1 pt-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={async () => {
                        const full = (await knowledgeApi.getArticle(a.id)).data;
                        setEditing({ ...full, tags: (full.tags || []).join(', ') });
                      }} data-testid={`edit-kb-${a.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/knowledge/${a.slug || a.id}`)} data-testid={`view-kb-${a.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!window.confirm('Delete article?')) return;
                        await knowledgeApi.deleteArticle(a.id); toast.success('Deleted'); await load();
                      }} className="text-[#991B1B]" data-testid={`delete-kb-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {articles.length === 0 && (
                <Card className="careiq-card col-span-full"><CardContent className="p-10 text-center text-[#64748B]">No articles yet.</CardContent></Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Button className="careiq-btn-primary" onClick={() => setEditingCat({ name: '', description: '', icon: 'BookOpen', order: 0 })} data-testid="new-kb-cat-btn">
            <FolderPlus className="w-4 h-4 mr-1" />New Category
          </Button>
          <div className="space-y-2">
            {categories.map(c => (
              <div key={c.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg bg-white" data-testid={`kb-cat-row-${c.id}`}>
                <div>
                  <div className="font-medium text-[#0A2342]">{c.name}</div>
                  <div className="text-xs text-[#64748B]">{c.description || '—'} · {c.article_count} articles</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditingCat(c)} data-testid={`edit-kb-cat-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={async () => {
                    if (!window.confirm('Delete category?')) return;
                    await knowledgeApi.deleteCategory(c.id); toast.success('Deleted'); await load();
                  }} data-testid={`delete-kb-cat-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
            {categories.length === 0 && <div className="p-6 text-center text-sm text-[#64748B] border border-dashed border-[#E2E8F0] rounded-lg">No categories yet.</div>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="kb-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit article' : 'New article'}</DialogTitle>
            <DialogDescription>Use the rich editor to embed images, videos, and HTML.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Article title" className="text-lg" data-testid="kb-title-input" />
              <Input value={editing.summary || ''} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} placeholder="One-line summary" data-testid="kb-summary-input" />
              <div className="grid grid-cols-3 gap-3">
                <Select value={editing.category_id || ''} onValueChange={(v) => setEditing({ ...editing, category_id: v || null })}>
                  <SelectTrigger data-testid="kb-cat-trigger"><SelectValue placeholder="No category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="kb-status-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={editing.tags || ''} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="tags, comma, sep" data-testid="kb-tags-input" />
              </div>
              <div>
                <div className="text-xs font-medium text-[#0A2342] mb-1">Featured image</div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs hover:bg-[#F8FAFC] cursor-pointer">
                    <Upload className="w-3.5 h-3.5" />Upload
                    <input type="file" accept="image/*" className="hidden" onChange={uploadFeaturedImage} data-testid="kb-feature-upload" />
                  </label>
                  <Input value={editing.featured_image_url || ''} onChange={(e) => setEditing({ ...editing, featured_image_url: e.target.value })} placeholder="…or paste URL" className="h-8 text-xs flex-1" data-testid="kb-feature-url-input" />
                </div>
                {editing.featured_image_url && <img src={editing.featured_image_url} alt="" className="mt-2 max-h-32 rounded-lg border border-[#E2E8F0]" />}
              </div>
              <RichContentEditor value={editing.content_html} onChange={(v) => setEditing({ ...editing, content_html: v })} placeholder="Start writing your article…" minHeight={320} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="kb-cancel-btn">Cancel</Button>
            <Button className="careiq-btn-primary" onClick={saveArticle} disabled={!editing?.title?.trim()} data-testid="kb-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingCat} onOpenChange={(v) => { if (!v) setEditingCat(null); }}>
        <DialogContent data-testid="kb-cat-dialog">
          <DialogHeader><DialogTitle>{editingCat?.id ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
          {editingCat && (
            <div className="space-y-3">
              <Input value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })} placeholder="Category name" data-testid="kb-cat-name-input" />
              <Textarea value={editingCat.description || ''} onChange={(e) => setEditingCat({ ...editingCat, description: e.target.value })} rows={2} placeholder="Description" data-testid="kb-cat-desc-input" />
              <Input type="number" value={editingCat.order || 0} onChange={(e) => setEditingCat({ ...editingCat, order: parseInt(e.target.value) || 0 })} placeholder="Display order" data-testid="kb-cat-order-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCat(null)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={saveCat} disabled={!editingCat?.name?.trim()} data-testid="kb-cat-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
